import { type FC } from 'react';
import { Controller, type SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';

import { useVerifyEmailMutation } from '@/api/queries/auth';
import { AppRoute } from '@/common/app/routes';
import { StringMain, StringXSmall } from '@/components/Typography/Typography';
import { Button } from '@/components/ui/Button/Button';
import { Input } from '@/components/ui/Input/Input';
import { useAppSelector } from '@/redux/store';

import { codeSchema } from './confit';

type ConfirmCodeFormTypy = {
  '1': number;
  '2': number;
  '3': number;
  '4': number;
};

const ConfirmCodeForm: FC = () => {
  const { t } = useTranslation();
  const [verifyEmail] = useVerifyEmailMutation();
  const email = useAppSelector((state) => state.auth.email);
  const navigate = useNavigate();
  const {
    control,
    setFocus,
    formState: { errors },
    setError,
    handleSubmit,
  } = useForm<ConfirmCodeFormTypy>({
    resolver: zodResolver(codeSchema),
    defaultValues: {
      '1': undefined,
      '2': undefined,
      '3': undefined,
      '4': undefined,
    },
    mode: 'onBlur',
  });

  const onSubmit: SubmitHandler<ConfirmCodeFormTypy> = async (
    data,
  ): Promise<void> => {
    try {
      if (!email) {
        throw new Error('Email is require');
      }
      const otp = Object.values(data).join('');
      await verifyEmail({ email, otp })
        .unwrap()
        .then(() => {
          navigate(AppRoute.MAIN);
        });
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'Something went wrong, please try again';

      setError('root', { message });
    }
  };

  const onError = (): void => {};

  return (
    <form
      className="w-full flex flex-col mb-4"
      onSubmit={handleSubmit(onSubmit, onError)}
    >
      <div className="w-full flex items-center gap-2 mb-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <Controller
            control={control}
            name={String(index + 1) as keyof ConfirmCodeFormTypy}
            render={({ field, fieldState: { error, isDirty } }) => (
              <Input
                {...field}
                onChange={(event) => {
                  const value = event.target.value;
                  field.onChange(parseInt(value));

                  if (!error && value.length === 1 && index < 3) {
                    setFocus(String(index + 2) as keyof ConfirmCodeFormTypy);
                  }
                }}
                className="w-[calc((100%_-_1.5rem)/4)] text-center"
                variant="code"
                hasError={!!error || !!errors.root}
                isValid={!error && isDirty && !errors.root}
                type="number"
              />
            )}
          />
        ))}
      </div>
      {errors.root && (
        <StringXSmall weight="bold" className="!text-red mb-4 text-center">
          {errors.root.message}
        </StringXSmall>
      )}
      <Button className="auth-btn" type="submit">
        <StringMain weight="bold" className="!text-main-bg">
          {t('Sign In')}
        </StringMain>
      </Button>
    </form>
  );
};

export { ConfirmCodeForm };
